/*
 * Copyright (c) 2023 Bluemate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useImperativeHandle, useRef, useState} from 'react';
import {ErrorType, nullify, ResponseData, showDialog} from '@dvrd/dvr-controls';
import {ActivateHandle, CancelType} from '../../../../util/interfaces';
import {setCancelReason} from '../../../../util/requestSender';
import CancelSubscription from './cancelSubscription';
import {createErrorMessage} from '../../../../util/utils';
import {onShowConfirmSnackbar} from '../../../../util/renderUtil';
import {trackPlatformAction} from '../../../../util/analytics';
import {AppContext} from '../../../misc/appContext';
import {useTranslation} from 'react-i18next';

interface Props {
    onSuccess?: VoidFunction;
    type: CancelType;
}

function CancelSubscriptionController(props: Props, ref: ForwardedRef<ActivateHandle>) {
    const context = useContext(AppContext);
    const {account} = context.accountContext;
    const {t} = useTranslation();
    const {type, onSuccess} = props;
    const [active, setActive] = useState(false);
    const [loading, setLoading] = useState(false);
    const [reason, setReason] = useState('');
    const reasonSaved = useRef<boolean>(false);
    const [error, setError] = useState<ErrorType>(null);

    function onClose() {
        setActive(false);
        setReason('');
    }

    function onChange(value: string) {
        setReason(value);
    }

    function onFinish(success: boolean) {
        return function () {
            if (reasonSaved.current && reason.length) {
                showDialog(
                    t('settings.subscription.thanksFeedback', 'Bedankt voor je feedback!'),
                    t('settings.subscription.thanksTitle', 'Bedankt'),
                    [
                        {
                            label: t('settings.subscription.ok', 'Oké'),
                            onClick: () => {
                                onClose();
                                if (onSuccess && success) onSuccess();
                            }
                        }
                    ]
                );
            } else {
                onClose();
                if (onSuccess && success) onSuccess();
            }
        };
    }

    function onResetError() {
        setError(null);
    }

    function onSubmit() {
        if (!validateValues()) return;
        if (!account) return onFinish(true)();
        setLoading(true);
        setCancelReason({
            data: {
                account_id: account.id,
                reason: nullify(reason),
                cancel_type: type
            },
            callback: (data: ResponseData) => {
                setLoading(false);
                if (data.success) {
                    trackPlatformAction('settings.subscription', 'cancel_subscription');
                    onShowConfirmSnackbar(
                        t('settings.subscription.subscriptionCanceled', 'Het abonnement is opgezegd')
                    );
                    reasonSaved.current = true;
                    onFinish(true)();
                } else
                    showDialog(
                        createErrorMessage(
                            data.message ??
                                t(
                                    'settings.subscription.cancelFailedMsg',
                                    'Het stoppen van het abonnement is niet gelukt.'
                                )
                        ),
                        t('settings.subscription.cancelFailedTitle', 'Stopzetten mislukt')
                    );
            }
        });
    }

    function validateValues(): boolean {
        if (!reason) {
            setError(
                t(
                    'settings.subscription.errFeedbackRequired',
                    'Graag nog wat feedback achter laten over waarom je weggaat.'
                )
            );
            return false;
        }
        return true;
    }

    function activate() {
        reasonSaved.current = false;
        setActive(true);
    }

    useImperativeHandle(ref, () => ({activate}));

    return (
        <CancelSubscription
            onSubmit={onSubmit}
            onCancel={onFinish(false)}
            onChange={onChange}
            reason={reason}
            active={active}
            loading={loading}
            type={type}
            error={error}
            onResetError={onResetError}
        />
    );
}

export default forwardRef<ActivateHandle, Props>(CancelSubscriptionController);
